import { prepararEstadoSomenteLeitura } from '@next/compartilhados/catalogos/apresentacao/estado-seletor-catalogo'

import type {
  ListaItens,
  SeletorItem,
} from '@Lar21/imaginii-web-components/vue'

import type {
  FiltrosCatalogoEmpregado,
  OpcaoEmpregado,
  SelecaoCatalogoEmpregado,
} from '../catalogo-empregado.tipos'

export const CHAVE_I18N_CATALOGO_EMPREGADO =
  'srcNext.estruturaDePontoDaEmpresa.catalogoEmpregado'

type Traduzir = (chave: string) => string

export const criarItemSeletorEmpregado = (
  opcao: OpcaoEmpregado,
): SeletorItem => ({
  chave: opcao.id,
  valor: opcao.name,
  ...(typeof opcao.code === 'string' && opcao.code.trim() ? { legenda: opcao.code } : {}),
})

const possuiIdRelacionado = (valor: unknown, id: string): boolean => (
  typeof valor === 'object' && valor !== null && 'id' in valor && valor.id === id
)

const normalizarOpcoesSelecionadas = (
  valor: SelecaoCatalogoEmpregado,
  selecaoMultipla: boolean,
): readonly OpcaoEmpregado[] => {
  if (valor === null) return []
  if (!Array.isArray(valor)) return [valor as OpcaoEmpregado]
  if (selecaoMultipla) return valor as readonly OpcaoEmpregado[]
  return valor[0] ? [valor[0] as OpcaoEmpregado] : []
}

export const filtrarOpcoesEmpregado = (
  opcoes: readonly OpcaoEmpregado[],
  filtros?: FiltrosCatalogoEmpregado,
  predicado?: (empregado: OpcaoEmpregado) => boolean,
): readonly OpcaoEmpregado[] => {
  if ((!filtros || Object.keys(filtros).length === 0) && !predicado) return opcoes
  return opcoes.filter(opcao => (
    (filtros?.id === undefined || (typeof opcao.id === 'string' && filtros.id.includes(opcao.id)))
    && (filtros?.company === undefined || (Array.isArray(opcao.company)
      && filtros.company.some(id => opcao.company?.some(local => possuiIdRelacionado(local, id)))))
    && (predicado === undefined || predicado(opcao))
  ))
}

export const criarEstadoSeletorCatalogoEmpregado = (
  opcoes: readonly OpcaoEmpregado[],
  valor: SelecaoCatalogoEmpregado,
  selecaoMultipla: boolean,
): ListaItens => {
  const carregados = opcoes.filter(opcao => typeof opcao.id === 'string' && opcao.id.trim()
    && typeof opcao.name === 'string' && opcao.name.trim()).map(criarItemSeletorEmpregado)
  const itensPorId = new Map(
    carregados.map((item) => [String(item.chave), item]),
  )
  const selecionados = normalizarOpcoesSelecionadas(valor, selecaoMultipla)
    .flatMap((opcao) => {
      const item = itensPorId.get(opcao.id)
      return item ? [item] : []
    })

  return { carregados, selecionados }
}

export const prepararEstadoSomenteLeituraEmpregado = (
  estado: ListaItens,
  somenteLeitura: boolean,
  selecao: SelecaoCatalogoEmpregado,
): ListaItens => {
  if (!somenteLeitura) return prepararEstadoSomenteLeitura(estado, false)
  const opcoes = normalizarOpcoesSelecionadas(selecao, true)
  return prepararEstadoSomenteLeitura(criarEstadoSeletorCatalogoEmpregado(opcoes, opcoes, true), true)
}

export const extrairValorDoSeletorCatalogoEmpregado = (
  estado: ListaItens,
  opcoes: readonly OpcaoEmpregado[],
  selecaoMultipla: boolean,
): SelecaoCatalogoEmpregado => {
  const opcoesPorId = new Map(opcoes.map((opcao) => [opcao.id, opcao]))
  const idsSelecionados = [...new Set(
    estado.selecionados.map(({ chave }) => String(chave)),
  )]
  const opcoesSelecionadas = idsSelecionados.flatMap((id) => {
    const opcao = opcoesPorId.get(id)
    return opcao ? [opcao] : []
  })
  return selecaoMultipla
    ? opcoesSelecionadas
    : (opcoesSelecionadas[0] ?? null)
}

export const criarPropriedadesPontotelSeletorEmpregado = ({
  traduzir,
  label,
  placeholder,
  erro,
}: Readonly<{
  traduzir: Traduzir
  label?: string
  placeholder?: string
  erro: string | null
}>) => ({
  tamanho: 'M' as const,
  largura: '100%',
  tamanhoPagina: 20,
  pesquisavel: true,
  recarregarTodos: true,
  label: label ?? traduzir(`${CHAVE_I18N_CATALOGO_EMPREGADO}.label`),
  placeholder: placeholder
    ?? traduzir(`${CHAVE_I18N_CATALOGO_EMPREGADO}.placeholder`),
  erro,
  traducaoNenhumResultado: traduzir(
    `${CHAVE_I18N_CATALOGO_EMPREGADO}.nenhumResultado`,
  ),
  traducaoSelecionado: traduzir(
    `${CHAVE_I18N_CATALOGO_EMPREGADO}.selecionado`,
  ),
  traducaoSelecionadosPlural: traduzir(
    `${CHAVE_I18N_CATALOGO_EMPREGADO}.selecionados`,
  ),
  traducaoRecarregarTodos: traduzir(
    `${CHAVE_I18N_CATALOGO_EMPREGADO}.recarregar`,
  ),
  traducaoRecarregado: traduzir(
    `${CHAVE_I18N_CATALOGO_EMPREGADO}.recarregado`,
  ),
})
